import { getSupabaseClient } from "../supabase/supabase";
import { getActiveWorkspaceId } from "../../../gravun/workspace/activeWorkspace";

export const ATTACHMENTS_BUCKET =
  import.meta.env.VITE_ATTACHMENTS_BUCKET || "attachments";

/**
 * How long a generated download link stays valid. Long enough to open a photo
 * or PDF from a record, short enough that a link pasted into a chat is not a
 * permanent hole. Storage RLS is the real boundary; expiry limits the blast
 * radius of a link that escapes.
 */
export const SIGNED_URL_TTL_SECONDS = 60 * 60;

/**
 * Storage path for a new attachment: `<workspace_id>/<uuid>.<ext>`.
 *
 * The leading workspace folder is not cosmetic, storage RLS reads it as the
 * tenant key (`storage.foldername(name)[1]`), so a file uploaded outside it is
 * unreachable by everyone, including the uploader.
 */
export const attachmentPath = (fileName: string): string => {
  const workspaceId = getActiveWorkspaceId();
  if (!workspaceId) {
    // Refuse rather than fall back to the bucket root: a root object was
    // exactly the pre-isolation shape, readable by every tenant.
    throw new Error("Cannot upload a file without an active workspace");
  }
  const parts = fileName.split(".");
  const extension = parts.length > 1 ? `.${parts.pop()}` : "";
  return `${workspaceId}/${crypto.randomUUID()}${extension}`;
};

/**
 * A time-limited URL for a stored object. The bucket is private, so this
 * replaces `getPublicUrl` everywhere, a public URL would defeat the
 * isolation the storage policies enforce.
 */
export const signedAttachmentUrl = async (
  path: string,
): Promise<string | null> => {
  const { data, error } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error) return null;
  return data?.signedUrl ?? null;
};
